// FLAGS: identity
// Where the Phase Two hand-offs live, and which language they are told to use.
//
// login.* and register.* exist ONLY on the .com base. The per-locale bases
// (openup-test.nl, .de, .fr, .com.es and their production equivalents) were
// never created, so deriving a hand-off host from the *active* locale sent four
// of the five languages to a hostname that does not resolve — a browser DNS
// error on register, and a silently dead button on login, because
// createSigninRequest throws while fetching metadata and the catch only calls
// onError (OI-25494). The base therefore comes from the English domain, and the
// language travels as a parameter instead of as a hostname.
//
// This is deliberately NOT how redirect_uri is built. That one must keep using
// the active locale domain so the user is returned to the site they started on,
// and all five of those hosts are registered redirect URIs on the realm client.
//
// Every Phase Two host is composed here, outwards from getPhaseTwoBaseDomain.
// No consumer builds its URL from another consumer's finished URL: the sign-up
// link and the 2FA client both start from getPhaseTwoRegisterOrigin, the same
// way ssoProviderHelper starts from getPhaseTwoBaseDomain to build login.<base>.

import { getHostForLanguage } from "@src/utils/hostLanguage";
import { LanguageCodeEnglishGb } from "@src/utils/languageHelpers";
import i18n from "i18next";

const DEFAULT_LANGUAGE = "en";

/**
 * The one base where login.* and register.* actually exist — `openup-test.com`
 * on test, `openup.com` on production. Null when no domain resolves (localhost,
 * or an environment with no English domain configured); callers already treat
 * null as "Phase Two is not reachable from here" and surface it via onError.
 */
export const getPhaseTwoBaseDomain = (): string | null => {
  // "localhost" has no base segment and joins to "" — null, not an empty
  // string, so callers can keep using a single falsy check.
  const base = getHostForLanguage(LanguageCodeEnglishGb)
    ?.split(".")
    .slice(1)
    .join(".");
  return base || null;
};

/**
 * The register app's origin, trailing slash included —
 * `https://register.openup-test.com/` on test. Both register-app consumers
 * compose from here: the sign-up link appends its `/api/locale` entry path, the
 * 2FA client appends `api/2fa/*`.
 *
 * Keep this separate from getPhaseTwoRegisterUrl. When the 2FA client took its
 * base from that function instead, the sign-up link's query string swallowed
 * the API path — `…/api/locale?lang=en&return=%2Fapi/2fa/status` — a route the
 * register app's CORS matcher does not cover, so the preflight failed, the real
 * request was never sent, and the profile page showed 2FA off for accounts that
 * had it on.
 *
 * Null when the base domain can't be resolved (e.g. localhost).
 */
export const getPhaseTwoRegisterOrigin = (): string | null => {
  const baseDomain = getPhaseTwoBaseDomain();
  return baseDomain ? `https://register.${baseDomain}/` : null;
};

/**
 * The active language as a bare subtag — `nl`, never `nl-NL`. Both the register
 * app's /api/locale route and Keycloak's ui_locales match against bare tags; a
 * region-qualified value fails that match silently and the user gets English,
 * which is the whole failure mode this module exists to avoid.
 */
export const getPhaseTwoLanguage = (): string =>
  i18n.language?.split("-")[0] || DEFAULT_LANGUAGE;
